import {
    Button,
    IButtonProperties,
    createButton,
} from '@vfde-brix/ws10/button';
import { HtmlId, ResultHtmlId } from '../enums/elementId';
import { AddressForm, FiberSupplierData } from 'Container/AvailabilityCheck/interface';
import { getAvcResultButtonProps } from 'Container/AvailabilityCheck/util/result';
import { getUpdatedPriceButtonProps } from '../util/components/buttonHelper';
import {
    createComponentById,
    NO_PATTERN_BUSINESS_LOGIC,
    toggleElementVisibilityById,
} from '@vfde-brix/ws10/core';
import { formIsValid } from '../util/validator';
import { LmsForm } from 'Container/LeadManagementForm/interface';
import { AvcResult, JarvisProduct } from '@vfde-sails/avc-core';
import { UserInfo } from 'Container/App/interface';
import { updateFloatingMobileNotificationButton } from './fullwidth-notification';
import { PreMarketingType } from '../enums/product';

/**
 * Create Brix button component.
 * @param wrapperId - ID of the button element.
 * @param onClick - OnClick event handler.
 * @returns Mounted button component or undefined if the element can't be found with the provided wrapperId.
 */
export const mountButton = (wrapperId: HtmlId, onClick?: Function) =>
    createComponentById(createButton, wrapperId, onClick ? {
        onClick: (e: Event) => {
            e.preventDefault();
            onClick();
        },
    } : NO_PATTERN_BUSINESS_LOGIC);

/**
 * Updates Brix button component.
 * @param button - Brix button component.
 * @param newProps - The button will be updated with these new properties.
 */
export const updateButton = (button: Button | null, newProps: Partial<IButtonProperties>) => {
    button?.update(newProps);
};

/**
 * Enables or disables the AVC or LMS form submit button based on the required form fields
 * @param comp - Mounted button element.
 * @param form - The form to validate
 */
export const updateSubmitButtonState = (comp: Button | null, form: AddressForm | LmsForm) =>
    comp?.update({ optDisabled: !formIsValid(form) });

/**
 * Update the avc result button.
 * @param comp - Mounted button element
 * @param tariffId - Id of the selected tariff
 * @param isGigakombi - Gigakombi chosen or not
 * @param product - Fastest available product
 */
export const updateAvcResultButton = (
    comp: Button | null,
    tariffId: string,
    isGigakombi: boolean,
    product?: JarvisProduct,
    preMarketingType?: PreMarketingType,
    fiberSupplierData?: FiberSupplierData,
) => {
    toggleElementVisibilityById(ResultHtmlId.BUTTON, preMarketingType !== PreMarketingType.BHS);

    comp?.update(getAvcResultButtonProps(tariffId, isGigakombi, product,
        preMarketingType, fiberSupplierData));
};

/**
 * Update the price button.
 * @param comp - Mounted button element.
 * @param floatingMobileComp - Mounted button element for mobile.
 * @param tariffId - Id of the selected tariff
 * @param tariff - Selected tariff
 * @param isGigakombi - Gigakombi chosen or not
 * @param avcResult - Result of the avc, if there is.
 * @param onClick - OnClick event.
 */
export const updatePriceButton = (
    comp: Button | null,
    floatingMobileComp: Button | null,
    tariff: JarvisProduct | undefined,
    isGigakombi: boolean,
    avcResult?: AvcResult,
    onClick?: Function,
    preMarketingType?: PreMarketingType,
    userInfo?: UserInfo,
)=>{
    const btnProps: Partial<IButtonProperties> = getUpdatedPriceButtonProps(
        tariff,
        isGigakombi,
        avcResult,
        onClick,
        preMarketingType,
        userInfo,
    );
    comp?.update(btnProps);
    // check the condition of the floating button in mobile view
    updateFloatingMobileNotificationButton(floatingMobileComp, btnProps, avcResult, tariff?.id, userInfo);
};
